/**
 * Pure rules for putting the boss invader on the Godot board with ONLY the
 * bridge messages the shipped export understands (no engine rebuild):
 *
 *  - the boss is marked by re-coloring its anchor planet's ownership ring
 *    (`set_owner`) in the boss color;
 *  - the anchor is made clickable by riding along in `set_choosable_planets`;
 *  - a click on the anchor that is NOT also a live travel option is a boss
 *    click (travel always wins when the anchor is a legal move).
 *
 * Board script constraints these rules encode (PrometheusBoard source):
 *  - `Planets.gd` only ever ADDS clickability for a non-empty list; an EMPTY
 *    list is the only thing that un-clicks planets — hence the clear-then-set
 *    message sequence;
 *  - `Planet.gd`'s `owner_color` is a typed GDScript `Color`, so a ring is
 *    cleared with transparent `#00000000`, never null/empty (a Nil assignment
 *    is a runtime error in the engine).
 */

export interface ChoosablePlanetsMessage {
  type: 'set_choosable_planets'
  payload: string[]
}

export const CLEAR_RING_COLOR = '#00000000'

/** The message sequence that makes exactly `travelKeys` (+ the boss anchor) clickable. */
export function buildChoosableMessages(
  travelKeys: string[],
  bossAnchor: string | null | undefined,
): ChoosablePlanetsMessage[] {
  const keys = [...travelKeys]
  if (bossAnchor && !keys.includes(bossAnchor)) {
    keys.push(bossAnchor)
  }
  const messages: ChoosablePlanetsMessage[] = [{ type: 'set_choosable_planets', payload: [] }]
  if (keys.length) {
    messages.push({ type: 'set_choosable_planets', payload: keys })
  }
  return messages
}

/** A board click that should open the boss attack UI instead of travelling. */
export function isBossAnchorClick(
  planetKey: string,
  boss: { alive: boolean, anchor_planet: string } | undefined | null,
  travelOptions: Record<string, unknown> | undefined | null,
): boolean {
  return !!boss?.alive && planetKey === boss.anchor_planet && !(planetKey in (travelOptions ?? {}))
}

/**
 * What the anchor's ring should show once the boss marker comes off: the
 * current owner's color if a living player owns the anchor, else transparent.
 */
export function bossMarkerRestoreColor(
  players: ReadonlyArray<{ planets: readonly string[], color: string, dead?: boolean }>,
  anchor: string,
): string {
  for (const player of players) {
    if (!player.dead && player.planets.includes(anchor)) {
      return player.color
    }
  }
  return CLEAR_RING_COLOR
}
